/**
 * Courses Page - Enhanced UI/UX Design with Progress
 */
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { BookOpen, Target, GraduationCap, Award } from 'lucide-react';

// CDN Logo URLs (DevIcon)
const LOGO_URLS = {
  REDHAT: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redhat/redhat-original.svg',
  LINUX: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/linux/linux-original.svg',
  ANSIBLE: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ansible/ansible-original.svg',
  DOCKER: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg',
  TERRAFORM: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/terraform/terraform-original.svg',
  GIT: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg',
  PYTHON: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg',
  C_LANG: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg',
  CPP: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cplusplus/cplusplus-original.svg',
  MYSQL: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg',
  JAVA: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg',
  KUBERNETES: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg',
};

// Logo Components using CDN images
const RedHatLogo = () => (
  <img src={LOGO_URLS.REDHAT} alt="Red Hat" className="w-8 h-8" />
);

const LinuxServerLogo = () => (
  <img src={LOGO_URLS.LINUX} alt="Linux" className="w-8 h-8" />
);

const AnsibleLogo = () => (
  <img src={LOGO_URLS.ANSIBLE} alt="Ansible" className="w-8 h-8" />
);

const DockerLogo = () => (
  <img src={LOGO_URLS.DOCKER} alt="Docker" className="w-8 h-8" />
);

const TerraformLogo = () => (
  <img src={LOGO_URLS.TERRAFORM} alt="Terraform" className="w-8 h-8" />
);

const GitLogo = () => (
  <img src={LOGO_URLS.GIT} alt="Git" className="w-8 h-8" />
);

const PythonLogo = () => (
  <img src={LOGO_URLS.PYTHON} alt="Python" className="w-8 h-8" />
);

const CLogo = () => (
  <img src={LOGO_URLS.C_LANG} alt="C" className="w-8 h-8" />
);

const CppLogo = () => (
  <img src={LOGO_URLS.CPP} alt="C++" className="w-8 h-8" />
);

const MySQLLogo = () => (
  <img src={LOGO_URLS.MYSQL} alt="MySQL" className="w-8 h-8" />
);

const JavaLogo = () => (
  <img src={LOGO_URLS.JAVA} alt="Java" className="w-8 h-8" />
);

const KubernetesLogo = () => (
  <img src={LOGO_URLS.KUBERNETES} alt="Kubernetes" className="w-8 h-8" />
);

// Course metadata configuration
const courseMetadata = {
  'RH124': {
    name: 'Red Hat System Administration I',
    gradient: 'from-red-500 via-red-600 to-red-700',
    bgPattern: 'bg-red-50',
    description: 'Master the fundamentals of Linux system administration. Perfect for IT professionals starting their Red Hat journey.',
    features: ['Command Line Mastery', 'File System Management', 'User Administration', 'Service Management'],
    level: 'Foundation',
    duration: '5 Days',
    difficulty: 'Beginner',
    logoComponent: RedHatLogo,
    certification: 'RHCSA Track',
    skills: ['Linux Basics', 'Command Line', 'File Operations', 'System Navigation']
  },
  'RH134': {
    name: 'Red Hat System Administration II',
    gradient: 'from-red-600 via-red-700 to-red-800',
    bgPattern: 'bg-red-50',
    description: 'Advanced system administration skills for professionals preparing for RHCSA certification and beyond.',
    features: ['Advanced Configuration', 'Security Implementation', 'Network Services', 'System Tuning'],
    level: 'Advanced',
    duration: '5 Days',
    difficulty: 'Advanced',
    logoComponent: RedHatLogo,
    certification: 'RHCSA Ready',
    skills: ['Advanced Config', 'Security', 'Networking', 'Performance']
  },
  'SVRADM': {
    name: 'Server Administration - Linux Services',
    gradient: 'from-purple-500 via-purple-600 to-purple-700',
    bgPattern: 'bg-purple-50',
    description: 'Master essential Linux server services - SSH, DNS, Web Servers, NFS, Samba, DHCP, FTP, Database & Firewall configuration.',
    features: ['SSH & DNS Server', 'Apache & Nginx', 'NFS & Samba', 'Database & Firewall'],
    level: 'Professional',
    duration: '5 Days',
    difficulty: 'Intermediate',
    logoComponent: LinuxServerLogo,
    certification: 'Server Admin',
    skills: ['Server Services', 'Web Servers', 'File Sharing', 'Security']
  },
  'ANSIBLE': {
    name: 'Automation with Ansible',
    gradient: 'from-gray-700 via-gray-800 to-gray-900',
    bgPattern: 'bg-gray-50',
    description: 'Master Ansible automation - Playbooks, Roles, Jinja2 Templates, Inventory Management, Handlers & Variables.',
    features: ['Playbooks & Roles', 'Jinja2 Templates', 'Inventory Management', 'Handlers & Loops'],
    level: 'Professional',
    duration: '5 Days',
    difficulty: 'Intermediate',
    logoComponent: AnsibleLogo,
    certification: 'RH294 Track',
    skills: ['Automation', 'Playbooks', 'Roles', 'Templates']
  },
  'DOCKER': {
    name: 'Docker Containers & DevOps',
    gradient: 'from-blue-500 via-blue-600 to-cyan-600',
    bgPattern: 'bg-blue-50',
    description: 'Master Docker containerization - Images, Volumes, Networks, Compose & orchestration for modern DevOps workflows.',
    features: ['Container Management', 'Docker Compose', 'Networking & Volumes', 'Image Building'],
    level: 'Professional',
    duration: '5 Days',
    difficulty: 'Intermediate',
    logoComponent: DockerLogo,
    certification: 'DevOps Track',
    skills: ['Containers', 'Docker Compose', 'Networking', 'CI/CD']
  },
  'TERRAFORM': {
    name: 'Terraform - Infrastructure as Code',
    gradient: 'from-violet-500 via-purple-600 to-violet-700',
    bgPattern: 'bg-violet-50',
    description: 'Master Terraform IaC - AWS Infrastructure, Modules, State Management, Variables & Real-world Cloud Projects.',
    features: ['AWS Infrastructure', 'Terraform Modules', 'State Management', 'VPC & EC2 Setup'],
    level: 'Professional',
    duration: '5 Days',
    difficulty: 'Intermediate',
    logoComponent: TerraformLogo,
    certification: 'Cloud Track',
    skills: ['IaC', 'AWS', 'Modules', 'State Mgmt'],
    isCloudCourse: true
  },
  'KUBERNETES': {
    name: 'Kubernetes - Container Orchestration (k3s)',
    gradient: 'from-blue-400 via-indigo-500 to-blue-700',
    bgPattern: 'bg-blue-50',
    description: 'Master Kubernetes with k3s - Pods, Deployments, Services, Ingress, Helm, RBAC, Troubleshooting & Full Stack Projects.',
    features: ['Pods & Deployments', 'Services & Ingress', 'Helm & RBAC', 'Troubleshooting'],
    level: 'Professional',
    duration: '5 Days',
    difficulty: 'Intermediate',
    logoComponent: KubernetesLogo,
    certification: 'DevOps Track',
    skills: ['k8s', 'Pods', 'Services', 'Helm']
  },
  'GIT': {
    name: 'Git & GitHub - Version Control',
    gradient: 'from-orange-500 via-orange-600 to-red-500',
    bgPattern: 'bg-orange-50',
    description: 'Master Git & GitHub - Branching, Merging, Pull Requests, Issues & Real-world Collaboration Workflows.',
    features: ['Git Fundamentals', 'Branching & Merging', 'Pull Requests', 'GitHub Collaboration'],
    level: 'Beginner',
    duration: '3 Days',
    difficulty: 'Easy',
    logoComponent: GitLogo,
    certification: 'DevOps Track',
    skills: ['Version Control', 'GitHub', 'PRs', 'Collaboration']
  },
  'EXAM_RHCSA': {
    name: 'RHCSA Practice Exam',
    gradient: 'from-red-600 via-red-700 to-red-800',
    bgPattern: 'bg-red-50',
    description: 'Complete RHCSA Practice Exams - 3 Full Exam Sets with 45 Real Questions, Dual VM Architecture & Auto Verification.',
    features: ['3 Complete Exams', '45 Real Questions', 'Auto Verification', 'Unlimited Practice'],
    level: 'Exam Prep',
    duration: 'Unlimited',
    difficulty: 'RHCSA Level',
    logoComponent: RedHatLogo,
    certification: 'RHCSA Exam',
    skills: ['Exam Practice', 'Real Questions', 'Auto Verify', 'VM Labs'],
    isExamCourse: true,
    examLink: '/exam/rhcsa'
  },
  'EXAM_DO188': {
    name: 'DO188 Container Exam',
    gradient: 'from-blue-500 via-blue-600 to-cyan-600',
    bgPattern: 'bg-blue-50',
    description: 'Complete DO188 Container Practice Exams - 3 Full Exam Sets with 45 Container Questions, Podman & Buildah Labs.',
    features: ['3 Complete Exams', '45 Container Tasks', 'Podman & Buildah', 'Container Registry'],
    level: 'Exam Prep',
    duration: 'Unlimited',
    difficulty: 'DO188 Level',
    logoComponent: RedHatLogo,
    certification: 'DO188 Exam',
    skills: ['Containers', 'Podman', 'Buildah', 'Systemd'],
    isExamCourse: true,
    examLink: '/exam/do188'
  },
  'EXAM_EX294': {
    name: 'EX294 Ansible Exam',
    gradient: 'from-gray-600 via-gray-700 to-gray-800',
    bgPattern: 'bg-gray-50',
    description: 'Complete EX294 Ansible Practice Exams - 3 Full Exam Sets with 18 Ansible Automation Questions.',
    features: ['3 Complete Exams', '18 Ansible Tasks', 'Playbooks & Roles', 'Vault & Galaxy'],
    level: 'Exam Prep',
    duration: 'Unlimited',
    difficulty: 'EX294/RHCE Level',
    logoComponent: AnsibleLogo,
    certification: 'EX294 Exam',
    skills: ['Ansible', 'Playbooks', 'Roles', 'Vault'],
    isExamCourse: true,
    examLink: '/exam/ex294'
  },
  'PYTHON': {
    name: 'Python Programming',
    gradient: 'from-yellow-400 via-yellow-500 to-yellow-600',
    bgPattern: 'bg-yellow-50',
    description: 'Master Python programming from basics to advanced - Variables, Functions, OOP, File I/O, Data Structures & Projects.',
    features: ['Python Basics', 'Functions & OOP', 'File Handling', 'Data Structures'],
    level: 'Beginner to Advanced',
    duration: '4 Days',
    difficulty: 'Easy',
    logoComponent: PythonLogo,
    certification: 'Programming',
    skills: ['Python', 'OOP', 'File I/O', 'DSA'],
    isProgrammingCourse: true
  },
  'C_LANG': {
    name: 'C Programming',
    gradient: 'from-blue-400 via-blue-500 to-blue-600',
    bgPattern: 'bg-blue-50',
    description: 'Master C programming - Variables, Pointers, Arrays, Strings, Structures, File I/O & Memory Management.',
    features: ['C Basics', 'Pointers & Arrays', 'Structures', 'Memory Management'],
    level: 'Beginner to Advanced',
    duration: '4 Days',
    difficulty: 'Intermediate',
    logoComponent: CLogo,
    certification: 'Programming',
    skills: ['C', 'Pointers', 'Memory', 'File I/O'],
    isProgrammingCourse: true
  },
  'CPP': {
    name: 'C++ Programming',
    gradient: 'from-indigo-500 via-purple-500 to-purple-600',
    bgPattern: 'bg-indigo-50',
    description: 'Master C++ programming - OOP Concepts, Classes, Inheritance, Polymorphism, STL & Modern C++ Features.',
    features: ['OOP Concepts', 'Classes & Inheritance', 'STL Containers', 'Smart Pointers'],
    level: 'Beginner to Advanced',
    duration: '5 Days',
    difficulty: 'Intermediate',
    logoComponent: CppLogo,
    certification: 'Programming',
    skills: ['C++', 'OOP', 'STL', 'Templates'],
    isProgrammingCourse: true
  },
  'MYSQL': {
    name: 'MySQL/MariaDB - Database Administration',
    gradient: 'from-orange-500 via-amber-500 to-orange-600',
    bgPattern: 'bg-orange-50',
    description: 'Master MySQL/MariaDB - Installation, CRUD, JOINs, Indexes, Stored Procedures, Triggers, Backup & Security Hardening.',
    features: ['CRUD & JOINs', 'Stored Procedures', 'Backup & Restore', 'Security Hardening'],
    level: 'Beginner to Advanced',
    duration: '5 Days',
    difficulty: 'Intermediate',
    logoComponent: MySQLLogo,
    certification: 'Database Admin',
    skills: ['SQL', 'MariaDB', 'Backup', 'Security'],
    isDatabaseCourse: true
  },
  'JAVA': {
    name: 'Core Java - Zero to Hero',
    gradient: 'from-red-500 via-red-600 to-orange-500',
    bgPattern: 'bg-red-50',
    description: 'Master Core Java from basics to advanced - OOP, Collections, File I/O, Multithreading, JDBC & Build Real Projects.',
    features: ['OOP & Inheritance', 'Collections Framework', 'Multithreading', 'JDBC Database'],
    level: 'Beginner to Advanced',
    duration: '7 Days',
    difficulty: 'Intermediate',
    logoComponent: JavaLogo,
    certification: 'Java Programming',
    skills: ['Java', 'OOP', 'JDBC', 'Collections'],
    isProgrammingCourse: true
  }
};

const Courses = () => {
  const [searchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'exams' ? 'exams' : 'courses');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch courses first (required)
      const coursesData = await apiService.getCourses();
      setCourses(coursesData);
      
      // Fetch progress separately (optional - don't fail if this errors)
      try {
        const progressData = await apiService.getMyProgress();
        setProgress(progressData);
      } catch (progressError) {
        setProgress([]); // Set empty progress, don't fail the whole page
      }
    } catch (error) {
      setError('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-24 sm:h-32 bg-gray-200 rounded-xl"></div>
            <div className="h-48 sm:h-64 bg-gray-200 rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md text-center">
          <p className="text-red-700">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const totalLabs = courses.reduce((sum, course) => sum + course.lab_count, 0);

  // Separate courses and exams
  const regularCourses = courses.filter(c => !courseMetadata[c.code]?.isExamCourse);
  const examCourses = courses.filter(c => courseMetadata[c.code]?.isExamCourse);
  const displayCourses = activeTab === 'courses' ? regularCourses : examCourses;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="courses-page">
      
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-blue-950/20"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="text-center">
            <div className="mb-6">
              <div className="flex items-center justify-center mb-3">
                <div className="bg-gradient-to-r from-emerald-400 to-cyan-400 p-2 rounded-full mr-3">
                  <span className="text-xl">🎓</span>
                </div>
                <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-white via-blue-100 to-cyan-200 bg-clip-text text-transparent">
                  Linux, DevOps, Database & Programming
                </h1>
                <div className="bg-gradient-to-r from-cyan-400 to-blue-400 p-2 rounded-full ml-3">
                  <span className="text-xl">⚡</span>
                </div>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-full px-6 py-2 inline-block border border-white/20">
                <p className="text-blue-100 text-sm">
                  Powered by <span className="text-emerald-300 font-bold">Grras Solutions</span> | Under the guidance of <span className="text-cyan-300 font-bold">Ravi Sir</span>
                </p>
              </div>
            </div>
            
            {/* Stats */}
            <div className="flex items-center justify-center space-x-12 mb-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/20">
                <div className="text-2xl font-bold text-cyan-300">{totalLabs}</div>
                <div className="text-xs text-blue-200">Interactive Labs</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/20">
                <div className="text-2xl font-bold text-emerald-300">{courses.length}</div>
                <div className="text-xs text-blue-200">Expert Courses</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/20">
                <div className="text-2xl font-bold text-yellow-300">100%</div>
                <div className="text-xs text-blue-200">Hands-on</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Courses List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="text-center mb-8">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-800 mb-2">
            Choose Your Learning Path
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-sm sm:text-base">
            Master Linux, DevOps & Programming with hands-on labs
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-white rounded-xl p-1.5 shadow-lg border border-slate-200">
            <button
              onClick={() => setActiveTab('courses')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-300 ${
                activeTab === 'courses'
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
              data-testid="courses-tab"
            >
              <BookOpen className="w-5 h-5" />
              <span>Courses</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'courses' ? 'bg-white/20' : 'bg-slate-200'
              }`}>
                {regularCourses.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('exams')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-300 ${
                activeTab === 'exams'
                  ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-md'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
              data-testid="exams-tab"
            >
              <Target className="w-5 h-5" />
              <span>Practice Exams</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'exams' ? 'bg-white/20' : 'bg-slate-200'
              }`}>
                {examCourses.length}
              </span>
            </button>
          </div>
        </div>

        {/* Tab Description */}
        {activeTab === 'exams' && (
          <div className="max-w-3xl mx-auto mb-8 bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-xl p-4 text-center">
            <p className="text-slate-700">
              <span className="font-semibold text-red-700">Practice Exams</span> - Real exam-style questions with VM-based labs and auto verification. 
              Perfect for certification preparation!
            </p>
          </div>
        )}

        {displayCourses.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {displayCourses.map((course, index) => {
              const metadata = courseMetadata[course.code] || {};
              const courseProgress = progress.filter(p => p.course === course.code);
              const completedLabs = courseProgress.filter(p => p.completed_tasks === p.total_tasks && p.total_tasks > 0).length;
              const totalLabs = course.lab_count;
              const progressLabel = `${completedLabs}/${totalLabs} labs`;
              const progressPercent = totalLabs > 0 ? (completedLabs / totalLabs) * 100 : 0;

              return (
                <div
                  key={course.code}
                  className="group bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden hover:shadow-2xl hover:-translate-y-1 transition-all duration-500"
                  data-testid={`course-card-${course.code}`}
                >
                  {/* Header */}
                  <div className={`relative bg-gradient-to-r ${metadata.gradient || 'from-slate-500 to-slate-600'} text-white p-4`}>
                    <div className="absolute inset-0 bg-black/10"></div>
                    
                    <div className="relative flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="bg-white/20 backdrop-blur-sm p-2 rounded-lg flex items-center justify-center">
                          {metadata.logoComponent ? (
                            <metadata.logoComponent />
                          ) : (
                            <BookOpen className="w-8 h-8 text-white" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="bg-white/30 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-bold">
                              {course.code}
                            </span>
                            <span className="bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full text-xs">
                              {metadata.difficulty || 'Course'}
                            </span>
                          </div>
                          <h3 className="text-lg font-bold leading-tight">
                            {metadata.name || course.title}
                          </h3>
                        </div>
                      </div>
                      <div className="text-center bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1">
                        <div className="text-xl font-bold">{course.lab_count}</div>
                        <div className="text-xs opacity-90">Labs</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-4">
                    <p className="text-slate-600 mb-4 text-sm leading-relaxed">
                      {metadata.description || course.description || 'Comprehensive hands-on training for professional development.'}
                    </p>
                    
                    {/* Skills */}
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-1">
                        {(metadata.skills || []).slice(0, 4).map((skill, idx) => (
                          <span 
                            key={idx} 
                            className={`px-2 py-1 rounded-full text-xs font-medium border ${metadata.bgPattern || 'bg-slate-50'} text-slate-700`}
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    {/* Progress - Hide for Exam Courses */}
                    {!metadata.isExamCourse && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-slate-700">Course Progress</span>
                        <span className="text-xs font-semibold text-emerald-600">{progressLabel}</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-1.5">
                        <div
                          className="bg-gradient-to-r from-emerald-500 to-emerald-600 h-1.5 rounded-full transition-all duration-500"
                          style={{ width: `${progressPercent}%` }}
                        ></div>
                      </div>
                    </div>
                    )}
                    
                    {/* Action */}
                    <Link
                      to={metadata.examLink || `/courses/${course.code}`}
                      className={`group/btn flex items-center justify-center w-full ${
                        metadata.isExamCourse 
                          ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800' 
                          : 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800'
                      } text-white py-3 rounded-lg font-semibold transition-all duration-300 text-sm shadow-lg hover:shadow-xl`}
                      data-testid={`view-labs-button-${course.code}`}
                    >
                      <span className="mr-2 flex items-center gap-1">
                        {metadata.isExamCourse ? (
                          <><Target className="w-4 h-4 inline" /> Start Exam Practice</>
                        ) : index === 0 ? 'Begin Journey' : 'Continue Learning'}
                      </span>
                      <span className="text-lg group-hover/btn:translate-x-1 transition-transform">→</span>
                    </Link>
                    
                    {/* Level */}
                    <div className="text-center mt-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        course.code === 'RH124' ? 'bg-red-100 text-red-700' : 
                        course.code === 'RH134' ? 'bg-red-100 text-red-800' :
                        course.code === 'SVRADM' ? 'bg-purple-100 text-purple-700' :
                        course.code === 'ANSIBLE' ? 'bg-gray-100 text-gray-700' :
                        course.code === 'DOCKER' ? 'bg-blue-100 text-blue-700' :
                        course.code === 'KUBERNETES' ? 'bg-indigo-100 text-indigo-700' :
                        course.code === 'TERRAFORM' ? 'bg-violet-100 text-violet-700' :
                        course.code === 'GIT' ? 'bg-orange-100 text-orange-700' :
                        course.code === 'PYTHON' ? 'bg-yellow-100 text-yellow-700' :
                        course.code === 'C_LANG' ? 'bg-blue-100 text-blue-700' :
                        course.code === 'CPP' ? 'bg-indigo-100 text-indigo-700' :
                        course.code === 'MYSQL' ? 'bg-orange-100 text-orange-700' :
                        course.code === 'JAVA' ? 'bg-red-100 text-red-700' :
                        course.code === 'EXAM_RHCSA' ? 'bg-red-100 text-red-700' :
                        course.code === 'EXAM_DO188' ? 'bg-blue-100 text-blue-700' :
                        course.code === 'EXAM_EX294' ? 'bg-gray-100 text-gray-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {course.code === 'RH124' ? <><GraduationCap className="w-3 h-3" /> Foundation Level</> : 
                         course.code === 'RH134' ? <><GraduationCap className="w-3 h-3" /> Advanced Level</> :
                         course.code === 'SVRADM' ? <><Award className="w-3 h-3" /> Professional Level</> :
                         course.code === 'ANSIBLE' ? <><Award className="w-3 h-3" /> Automation Pro</> :
                         course.code === 'DOCKER' ? <><Award className="w-3 h-3" /> Container Pro</> :
                         course.code === 'KUBERNETES' ? <><Award className="w-3 h-3" /> K8s DevOps</> :
                         course.code === 'TERRAFORM' ? <><Award className="w-3 h-3" /> Cloud & IaC</> :
                         course.code === 'GIT' ? <><GraduationCap className="w-3 h-3" /> Version Control</> :
                         course.code === 'PYTHON' ? <><GraduationCap className="w-3 h-3" /> Programming</> :
                         course.code === 'C_LANG' ? <><GraduationCap className="w-3 h-3" /> Programming</> :
                         course.code === 'CPP' ? <><GraduationCap className="w-3 h-3" /> Programming</> :
                         course.code === 'MYSQL' ? <><Award className="w-3 h-3" /> Database Admin</> :
                         course.code === 'JAVA' ? <><GraduationCap className="w-3 h-3" /> Programming</> :
                         course.code === 'EXAM_RHCSA' ? <><Target className="w-3 h-3" /> Exam Preparation</> :
                         course.code === 'EXAM_DO188' ? <><Target className="w-3 h-3" /> Container Exam</> :
                         course.code === 'EXAM_EX294' ? <><Target className="w-3 h-3" /> Ansible Exam</> :
                         <><BookOpen className="w-3 h-3" /> Course Level</>}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl p-12 max-w-md mx-auto">
              <div className="flex justify-center mb-6">
                {activeTab === 'exams' ? <Target className="w-16 h-16 text-slate-400" /> : <BookOpen className="w-16 h-16 text-slate-400" />}
              </div>
              <h2 className="text-xl font-semibold text-slate-700 mb-3">
                {activeTab === 'exams' ? 'No practice exams available yet' : 'No courses available'}
              </h2>
              <p className="text-slate-500">
                {activeTab === 'exams' 
                  ? 'Practice exams are being prepared. Check back soon!' 
                  : 'New courses are being added regularly. Check back soon!'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Courses;

